'use client';

import Inventory2Icon from '@mui/icons-material/Inventory2';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useNotification } from '@/contexts/NotificationContext';
import {
  applySiteVisitInventory,
  queryMachineInventoryBalances,
  removeSiteVisitInventoryLine,
  resolveTomVendingInventoryUpc,
  syncTomVendingSiteVisit,
} from '@/hooks/useTomVendingInventory';
import { useInventoryRoomSync } from '@/hooks/useInventoryRoomSync';
import type { InventoryCountUpdatedEvent } from '@/realtime/inventorySocket';
import { useAuth } from '@/contexts/AuthContext';
import type { TomVendingMachine } from '@/hooks/useTomVendingMachine';

type TallyTab = 'par' | 'fill' | 'reject' | 'recycle';

export interface SiteVisitCommitted {
  par: number;
  fill: number;
  reject: number;
  recycle: number;
}

export interface SiteVisitLine {
  rowKey: string;
  balanceId: number;
  inventoryItemId: number;
  upc: string;
  name: string | null;
  thumbnailUrl?: string | null;
  imageUrl?: string | null;
  /** Booked qty on machine (ledger). */
  ledgerBookQty: number;
  /** Last saved tally snapshot from API (pending overlay). */
  committed: SiteVisitCommitted;
  parQuantity: number;
  fillDraft: number;
  rejectDraft: number;
  recycleDraft: number;
}

function lineIsFreshLocal(line: SiteVisitLine): boolean {
  return line.rowKey.includes('-local-');
}

function lineIsDirty(line: SiteVisitLine): boolean {
  return (
    line.parQuantity !== line.committed.par ||
    line.fillDraft !== line.committed.fill ||
    line.rejectDraft !== line.committed.reject ||
    line.recycleDraft !== line.committed.recycle
  );
}

function normalizeInventoryCode(raw: string): string {
  return raw.replace(/\D/g, '');
}

function gs1CheckDigit(bodyWithoutCheck: string): string {
  const body = bodyWithoutCheck.replace(/\D/g, '');
  if (!body) return '';
  let sum = 0;
  for (let i = 0; i < body.length; i++) {
    const n = body.charCodeAt(body.length - 1 - i) - 48;
    sum += n * (i % 2 === 0 ? 3 : 1);
  }
  return String((10 - (sum % 10)) % 10);
}

function hasValidGs1CheckDigit(full: string): boolean {
  const d = full.replace(/\D/g, '');
  if (![8, 12, 13, 14].includes(d.length)) return false;
  if (gs1CheckDigit(d.slice(0, -1)) === d.slice(-1)) return true;
  if (d.length === 13 && d.startsWith('0')) {
    const upcA = d.slice(1);
    return gs1CheckDigit(upcA.slice(0, -1)) === upcA.slice(-1);
  }
  return false;
}

function toGtin14(raw: string): string {
  let digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length > 14) digits = digits.slice(-14);
  return digits.padStart(14, '0');
}

function normalizeUpcForLookupLocal(raw: string): string {
  let digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length > 13) digits = digits.slice(-13);
  if (digits.length === 12) return `0${digits}`;
  return digits;
}

/** Expand validated 8-digit UPC-E to UPC-A; null when not a real UPC-E pair. */
function expandUpcEToUpcA(raw: string): string | null {
  const d = raw.replace(/\D/g, '');
  if (d.length !== 8) return null;
  const ns = d[0];
  if (ns !== '0' && ns !== '1') return null;
  const d1 = d[1];
  const d2 = d[2];
  const d3 = d[3];
  const d4 = d[4];
  const d5 = d[5];
  const d6 = d[6];
  const check = d[7];
  let body11: string;
  if (d6 === '0' || d6 === '1' || d6 === '2') {
    body11 = `${ns}${d1}${d2}${d6}0000${d3}${d4}${d5}`;
  } else if (d6 === '3') {
    body11 = `${ns}${d1}${d2}${d3}00000${d4}${d5}`;
  } else if (d6 === '4') {
    body11 = `${ns}${d1}${d2}${d3}${d4}00000${d5}`;
  } else {
    body11 = `${ns}${d1}${d2}${d3}${d4}${d5}0000${d6}`;
  }
  const expected = gs1CheckDigit(body11);
  if (expected !== check) return null;
  return body11 + expected;
}

/** Compress UPC-A / EAN-13 to UPC-E when the middle-zero pattern allows it. */
function compressUpcAToUpcE(raw: string): string | null {
  let d = raw.replace(/\D/g, '');
  if (d.length === 13 && d.startsWith('0')) d = d.slice(1);
  if (d.length !== 12) return null;
  if (!hasValidGs1CheckDigit(d)) return null;
  const ns = d[0];
  if (ns !== '0' && ns !== '1') return null;
  const b = d.slice(0, 11);
  let data6: string | null = null;
  if (/^\d[0-9]{2}[0-2]0000\d{3}$/.test(b)) {
    data6 = `${b[1]}${b[2]}${b[8]}${b[9]}${b[10]}${b[3]}`;
  } else if (/^\d[0-9]{3}00000\d{2}$/.test(b)) {
    data6 = `${b[1]}${b[2]}${b[3]}${b[9]}${b[10]}3`;
  } else if (/^\d[0-9]{4}00000\d$/.test(b)) {
    data6 = `${b[1]}${b[2]}${b[3]}${b[4]}${b[10]}4`;
  } else if (/^\d[0-9]{5}0000[5-9]$/.test(b)) {
    data6 = `${b[1]}${b[2]}${b[3]}${b[4]}${b[5]}${b[10]}`;
  }
  if (!data6 || data6.length !== 6) return null;
  const upcE = `${ns}${data6}${d[11]}`;
  if (expandUpcEToUpcA(upcE) !== d) return null;
  return upcE;
}

/** Mirror of server `gtinLookupCandidates`: GTIN-14 identity + validated check-digit neighbors + UPC-E. */
function gtinLookupVariants(digits: string): string[] {
  const raw = digits.replace(/\D/g, '');
  if (!raw) return [];
  const out: string[] = [];
  const add = (s: string) => {
    if (s.length > 0 && !out.includes(s)) out.push(s);
  };
  const canonical = normalizeUpcForLookupLocal(raw);
  add(canonical);

  const identities = new Set<string>();
  const addId = (s: string) => {
    const g = toGtin14(s);
    if (g) identities.add(g);
  };
  addId(raw);
  addId(canonical);

  const considerCheckNeighbors = (full: string) => {
    if (!full) return;
    if (hasValidGs1CheckDigit(full)) {
      const body = full.slice(0, -1);
      addId(body);
      add(body);
      if (full.length === 13 && full.startsWith('0') && hasValidGs1CheckDigit(full.slice(1))) {
        const upcBody = full.slice(1, -1);
        addId(upcBody);
        add(upcBody);
      }
    }
    if ((full.length === 11 || full.length === 12) && !hasValidGs1CheckDigit(full)) {
      const withCheck = full + gs1CheckDigit(full);
      addId(withCheck);
      add(normalizeUpcForLookupLocal(withCheck));
    }
  };
  considerCheckNeighbors(raw);
  considerCheckNeighbors(canonical);
  if (canonical.length === 13 && canonical.startsWith('0')) {
    considerCheckNeighbors(canonical.slice(1));
  }

  const expanded = expandUpcEToUpcA(raw);
  if (expanded) {
    add(expanded);
    add(normalizeUpcForLookupLocal(expanded));
    addId(expanded);
    considerCheckNeighbors(expanded);
  }
  const compressed =
    compressUpcAToUpcE(raw) ??
    compressUpcAToUpcE(canonical) ??
    (canonical.length === 13 && canonical.startsWith('0') ? compressUpcAToUpcE(canonical.slice(1)) : null);
  if (compressed) {
    add(compressed);
    addId(compressed);
  }

  for (const g14 of identities) {
    add(g14.slice(-13));
    add(g14.slice(-12));
    add(g14);
    const significant = g14.replace(/^0+/, '') || '0';
    add(normalizeUpcForLookupLocal(significant));
    add(normalizeUpcForLookupLocal(g14));
    add(significant);
  }
  for (const c of [...out]) {
    if (c.length === 12) add(`0${c}`);
    else if (c.length === 13 && c.startsWith('0')) add(c.slice(1));
  }
  return out;
}

function inventoryCodesMatch(a: string, b: string): boolean {
  const da = normalizeInventoryCode(a);
  const db = normalizeInventoryCode(b);
  if (!da || !db) return false;
  if (da === db) return true;
  if (toGtin14(da) === toGtin14(db)) return true;
  return gtinLookupVariants(da).includes(db) || gtinLookupVariants(db).includes(da);
}

function parseNonNegInt(raw: string): number {
  const n = parseInt(raw.replace(/[^\d]/g, '') || '0', 10);
  return Number.isNaN(n) || n < 0 ? 0 : n;
}

const MACHINE_FETCH_TAKE = 5000;
const TV_TALLY_AUTO_SAVE_MS = 750;

export type TomsVendingSiteVisitSectionHandle = {
  flushPendingSaves: () => Promise<boolean>;
};

interface TomsVendingSiteVisitSectionProps {
  /** Required to persist snapshot alongside pending save — session from `start-or-get` + `set-location`. */
  siteVisitId: number | null;
  /** From site visit payload; finalized visits are view-only until a new session. */
  visitStatus?: string;
  locationId: number;
  machines: TomVendingMachine[];
  onApplied?: () => void;
}

const TomsVendingSiteVisitSection = forwardRef<TomsVendingSiteVisitSectionHandle, TomsVendingSiteVisitSectionProps>(
  (
    {
      siteVisitId,
      visitStatus = 'open',
      locationId,
      machines,
      onApplied,
    },
    ref,
  ) => {
  const { showError } = useNotification();
  const { user } = useAuth();
  const currentUserId =
    user?.id != null ? parseInt(String(user.id), 10) : user?.user?.id != null ? parseInt(String(user.user.id), 10) : null;
  const lastSyncAtRef = useRef(new Date(0).toISOString());
  const [machineId, setMachineId] = useState<number | ''>('');
  const [tallyTab, setTallyTab] = useState<TallyTab>('par');
  const [lines, setLines] = useState<SiteVisitLine[]>([]);
  const linesRef = useRef(lines);
  linesRef.current = lines;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const saveTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const [manualUpc, setManualUpc] = useState('');
  const [removeTargetItemId, setRemoveTargetItemId] = useState<number | null>(null);
  const [removeSubmitting, setRemoveSubmitting] = useState(false);

  useEffect(() => {
    if (machines.length === 0) {
      setMachineId('');
      return;
    }
    setMachineId((prev) => {
      if (prev === '') return machines[0].id;
      if (machines.some((m) => m.id === prev)) return prev;
      return machines[0].id;
    });
  }, [machines]);

  const mergePreservedDrafts = useCallback((previous: SiteVisitLine[], fresh: SiteVisitLine[]): SiteVisitLine[] => {
    const freshKeys = new Set(fresh.map((l) => `${l.inventoryItemId}`));
    let out = [...fresh];
    for (const old of previous) {
      if (!lineIsDirty(old) && !lineIsFreshLocal(old)) continue;
      const key = `${old.inventoryItemId}`;
      if (freshKeys.has(key)) {
        const idx = out.findIndex((l) => l.inventoryItemId === old.inventoryItemId);
        if (idx >= 0) {
          out[idx] = {
            ...out[idx],
            parQuantity: old.parQuantity,
            fillDraft: old.fillDraft,
            rejectDraft: old.rejectDraft,
            recycleDraft: old.recycleDraft,
          };
        }
      } else if (lineIsFreshLocal(old)) {
        out = [...out, old];
      }
    }
    return out;
  }, []);

  const loadLines = useCallback(
    async (opts?: { preserveDrafts?: boolean }) => {
      if (machineId === '' || typeof machineId !== 'number') {
        setLines([]);
        return;
      }
      setLoading(true);
      try {
        const r = await queryMachineInventoryBalances({
          vendingMachineId: machineId,
          locationId,
          skip: 0,
          take: MACHINE_FETCH_TAKE,
          search: '',
          orderBy: 'upc',
          orderDirection: 'ASC',
          hideZeroQuantity: false,
          effectiveOpenPendingAcrossLocation: true,
        });
        const fresh: SiteVisitLine[] = r.data.map((row) => {
          const item = row.inventoryItem;
          const srcThumb = item.thumbnailUrl || item.imageUrl;
          const ledger = row.ledgerQuantity ?? row.quantity;
          const p = row.pending;
          const committed: SiteVisitCommitted = {
            par: p?.parQty ?? 0,
            fill: p?.fillQty ?? 0,
            reject: p?.rejectQty ?? 0,
            recycle: p?.recycleQty ?? 0,
          };
          return {
            rowKey: `${machineId}-${item.id}`,
            balanceId: row.balanceId,
            inventoryItemId: item.id,
            upc: item.upc,
            name: item.name,
            thumbnailUrl: srcThumb,
            imageUrl: item.imageUrl,
            ledgerBookQty: ledger,
            committed,
            parQuantity: committed.par,
            fillDraft: committed.fill,
            rejectDraft: committed.reject,
            recycleDraft: committed.recycle,
          };
        });
        setLines((prev) => (opts?.preserveDrafts ? mergePreservedDrafts(prev, fresh) : fresh));
      } catch (e: unknown) {
        showError(e instanceof Error ? e.message : 'Failed to load machine inventory');
        setLines([]);
      } finally {
        setLoading(false);
      }
    },
    [locationId, machineId, mergePreservedDrafts, showError],
  );

  const applyRemoteTally = useCallback(
    (
      inventoryItemId: number,
      tallies: SiteVisitCommitted | null,
      removed: boolean,
      updatedAt: string,
    ) => {
      const timerKey = String(inventoryItemId);
      if (saveTimersRef.current[timerKey]) return;
      const existing = linesRef.current.find((l) => l.inventoryItemId === inventoryItemId);
      if (existing && lineIsDirty(existing)) return;

      if (removed) {
        setLines((prev) => prev.filter((l) => l.inventoryItemId !== inventoryItemId));
      } else if (tallies) {
        setLines((prev) => {
          const idx = prev.findIndex((l) => l.inventoryItemId === inventoryItemId);
          if (idx < 0) return prev;
          const old = prev[idx];
          if (lineIsDirty(old)) return prev;
          const next = [...prev];
          next[idx] = {
            ...old,
            committed: tallies,
            parQuantity: tallies.par,
            fillDraft: tallies.fill,
            rejectDraft: tallies.reject,
            recycleDraft: tallies.recycle,
          };
          return next;
        });
      }
      if (updatedAt > lastSyncAtRef.current) {
        lastSyncAtRef.current = updatedAt;
      }
    },
    [],
  );

  const handleRemoteTvEvent = useCallback(
    (event: InventoryCountUpdatedEvent) => {
      if (event.domain !== 'tv_site_visit') return;
      if (machineId === '' || typeof machineId !== 'number') return;
      if (event.vendingMachineId != null && event.vendingMachineId !== machineId) return;
      if (event.removed) {
        applyRemoteTally(event.inventoryItemId, null, true, event.updatedAt);
        return;
      }
      applyRemoteTally(
        event.inventoryItemId,
        {
          par: event.parQty ?? 0,
          fill: event.fillQty ?? 0,
          reject: event.rejectQty ?? 0,
          recycle: event.recycleQty ?? 0,
        },
        false,
        event.updatedAt,
      );
    },
    [machineId, applyRemoteTally],
  );

  const pollTvSync = useCallback(async () => {
    if (machineId === '' || typeof machineId !== 'number') return;
    const since = lastSyncAtRef.current;
    const r = await syncTomVendingSiteVisit({
      locationId,
      since,
      vendingMachineId: machineId,
    });
    for (const line of r.lines) {
      if (line.vendingMachineId !== machineId) continue;
      applyRemoteTally(
        line.inventoryItemId,
        {
          par: line.parQty,
          fill: line.fillQty,
          reject: line.rejectQty,
          recycle: line.recycleQty,
        },
        false,
        line.updatedAt,
      );
    }
    const maxAt = r.lines.reduce((m, l) => (l.updatedAt > m ? l.updatedAt : m), since);
    if (maxAt > since) {
      lastSyncAtRef.current = maxAt;
    }
  }, [locationId, machineId, applyRemoteTally]);

  useInventoryRoomSync({
    enabled: visitStatus === 'open' && siteVisitId != null && siteVisitId > 0,
    join:
      machineId !== '' && typeof machineId === 'number'
        ? { scope: 'tv_site_visit', locationId, vendingMachineId: machineId }
        : { scope: 'tv_site_visit', locationId },
    currentUserId,
    onRemoteUpdate: handleRemoteTvEvent,
    poll: pollTvSync,
  });

  useEffect(() => {
    void loadLines({ preserveDrafts: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload full sheet when machine changes
  }, [machineId, locationId, siteVisitId]);

  const selectedMachine = useMemo(
    () => (typeof machineId === 'number' ? machines.find((m) => m.id === machineId) ?? null : null),
    [machineId, machines],
  );

  const saveLine = useCallback(
    async (line: SiteVisitLine): Promise<boolean> => {
      if (visitStatus !== 'open') return true;
      if (machineId === '' || typeof machineId !== 'number') return false;
      if (siteVisitId == null || siteVisitId <= 0) {
        showError('Site visit session not ready — refresh this page.');
        return false;
      }
      if (!lineIsDirty(line)) return true;
      setSaving(true);
      try {
        await applySiteVisitInventory({
          siteVisitId,
          vendingMachineId: machineId,
          locationId,
          lines: [
            {
              inventoryItemId: line.inventoryItemId,
              recycleQty: line.recycleDraft,
              rejectQty: line.rejectDraft,
              parQty: line.parQuantity,
              fillQty: line.fillDraft,
            },
          ],
        });
        setLines((prev) =>
          prev.map((l) =>
            l.inventoryItemId === line.inventoryItemId
              ? {
                  ...l,
                  committed: {
                    par: l.parQuantity,
                    fill: l.fillDraft,
                    reject: l.rejectDraft,
                    recycle: l.recycleDraft,
                  },
                }
              : l,
          ),
        );
        onApplied?.();
        return true;
      } catch (e: unknown) {
        showError(e instanceof Error ? e.message : 'Save failed');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [machineId, siteVisitId, locationId, showError, onApplied, visitStatus],
  );

  const flushPendingSaves = useCallback(async (): Promise<boolean> => {
    for (const t of Object.values(saveTimersRef.current)) {
      window.clearTimeout(t);
    }
    saveTimersRef.current = {};
    const dirty = linesRef.current.filter(lineIsDirty);
    for (const line of dirty) {
      const ok = await saveLine(line);
      if (!ok) return false;
    }
    return true;
  }, [saveLine]);

  useImperativeHandle(ref, () => ({ flushPendingSaves }), [flushPendingSaves]);

  const scheduleSaveLine = useCallback(
    (line: SiteVisitLine) => {
      if (visitStatus !== 'open') return;
      const key = String(line.inventoryItemId);
      const existing = saveTimersRef.current[key];
      if (existing) window.clearTimeout(existing);
      saveTimersRef.current[key] = window.setTimeout(() => {
        delete saveTimersRef.current[key];
        void saveLine(line);
      }, TV_TALLY_AUTO_SAVE_MS);
    },
    [saveLine, visitStatus],
  );

  const updateLineField = (
    inventoryItemId: number,
    patch: Partial<Pick<SiteVisitLine, 'parQuantity' | 'fillDraft' | 'rejectDraft' | 'recycleDraft'>>,
  ) => {
    setLines((prev) => {
      const next = prev.map((l) => (l.inventoryItemId === inventoryItemId ? { ...l, ...patch } : l));
      const updated = next.find((l) => l.inventoryItemId === inventoryItemId);
      if (updated && lineIsDirty(updated)) {
        scheduleSaveLine(updated);
      }
      return next;
    });
  };

  const tallyColumnHeader = useMemo(() => {
    switch (tallyTab) {
      case 'par':
        return 'Par (arrival)';
      case 'fill':
        return 'Fill (warehouse)';
      case 'reject':
        return 'Reject';
      case 'recycle':
        return 'Recycle';
      default:
        return '';
    }
  }, [tallyTab]);

  const handleManualLookup = async () => {
    if (machineId === '' || typeof machineId !== 'number') {
      showError('Select a machine first');
      return;
    }
    const n = normalizeInventoryCode(manualUpc);
    if (!n) return;

    const existingIdx = lines.findIndex((l) => inventoryCodesMatch(l.upc, n));
    if (existingIdx >= 0) {
      setManualUpc('');
      return;
    }

    try {
      const res = await resolveTomVendingInventoryUpc(manualUpc, { autoCreateInCatalog: true });
      if (!res.found || res.inventoryItemId == null) {
        showError(
          'No catalog match for this UPC. Add it in Catalog admin, or scan again when product data is available.',
        );
        return;
      }
      const iid = res.inventoryItemId;
      if (lines.some((l) => l.inventoryItemId === iid)) {
        setManualUpc('');
        return;
      }
      const primary = res.primaryUpc ?? manualUpc.trim();
      const thumb = res.thumbnailUrl || res.imageUrl;
      const z: SiteVisitCommitted = { par: 0, fill: 0, reject: 0, recycle: 0 };
      const row: SiteVisitLine = {
        rowKey: `${machineId}-${iid}-local-${globalThis.crypto?.randomUUID?.() ?? String(Date.now())}`,
        balanceId: 0,
        inventoryItemId: iid,
        upc: primary,
        name: res.name ?? null,
        thumbnailUrl: thumb,
        imageUrl: res.imageUrl,
        ledgerBookQty: 0,
        committed: z,
        parQuantity: 0,
        fillDraft: 0,
        rejectDraft: 0,
        recycleDraft: 0,
      };
      setLines((prev) => [...prev, row]);
      setManualUpc('');
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Lookup failed');
    }
  };

  const linePendingRemoveDetail =
    removeTargetItemId != null ? lines.find((l) => l.inventoryItemId === removeTargetItemId) : undefined;

  const handleConfirmRemoveRow = async () => {
    if (removeTargetItemId == null || machineId === '' || typeof machineId !== 'number') return;
    const line = lines.find((l) => l.inventoryItemId === removeTargetItemId);
    if (!line) {
      setRemoveTargetItemId(null);
      return;
    }
    if (siteVisitId == null || siteVisitId <= 0) {
      showError('Site visit session not ready — refresh this page.');
      setRemoveTargetItemId(null);
      return;
    }
    setRemoveSubmitting(true);
    try {
      if (lineIsFreshLocal(line)) {
        setLines((prev) => prev.filter((x) => x.inventoryItemId !== line.inventoryItemId));
      } else {
        await removeSiteVisitInventoryLine({
          siteVisitId,
          vendingMachineId: machineId,
          locationId,
          inventoryItemId: line.inventoryItemId,
        });
        setLines((prev) => prev.filter((x) => x.inventoryItemId !== line.inventoryItemId));
      }
      setRemoveTargetItemId(null);
      onApplied?.();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Remove failed');
    } finally {
      setRemoveSubmitting(false);
    }
  };

  const renderQtyField = (line: SiteVisitLine) => {
    const common = { size: 'small' as const, sx: { width: 100 }, disabled: visitStatus !== 'open' };
    switch (tallyTab) {
      case 'par':
        return (
          <TextField
            {...common}
            type="text"
            inputMode="numeric"
            value={line.parQuantity === 0 ? '' : String(line.parQuantity)}
            placeholder="0"
            onChange={(e) => updateLineField(line.inventoryItemId, { parQuantity: parseNonNegInt(e.target.value) })}
          />
        );
      case 'fill':
        return (
          <TextField
            {...common}
            type="text"
            inputMode="numeric"
            value={line.fillDraft === 0 ? '' : String(line.fillDraft)}
            placeholder="0"
            onChange={(e) => updateLineField(line.inventoryItemId, { fillDraft: parseNonNegInt(e.target.value) })}
          />
        );
      case 'reject':
        return (
          <TextField
            {...common}
            type="text"
            inputMode="numeric"
            value={line.rejectDraft === 0 ? '' : String(line.rejectDraft)}
            placeholder="0"
            onChange={(e) => updateLineField(line.inventoryItemId, { rejectDraft: parseNonNegInt(e.target.value) })}
          />
        );
      case 'recycle':
        return (
          <TextField
            {...common}
            type="text"
            inputMode="numeric"
            value={line.recycleDraft === 0 ? '' : String(line.recycleDraft)}
            placeholder="0"
            onChange={(e) => updateLineField(line.inventoryItemId, { recycleDraft: parseNonNegInt(e.target.value) })}
          />
        );
      default:
        return null;
    }
  };

  if (machines.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Add a vending machine to this location before recording a site visit.
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Par, Fill, Reject, and Recycle tallies save automatically as you edit them. Fill deducts warehouse stock when
        saved. Finalize posts any leftover Fill and closes the visit. Machine balances reflect the projected ending
        (par + fill − reject − recycle).
      </Typography>

      <FormControl fullWidth size="small" sx={{ maxWidth: 420, mb: 2 }}>
        <InputLabel id="site-visit-machine-label">Vending machine</InputLabel>
        <Select
          labelId="site-visit-machine-label"
          label="Vending machine"
          value={machineId === '' ? '' : machineId}
          onChange={(e: SelectChangeEvent<number>) => setMachineId(Number(e.target.value))}
        >
          {machines.map((m) => (
            <MenuItem key={m.id} value={m.id}>
              {m.assetId}
              {m.title?.trim() ? ` — ${m.title.trim()}` : ''}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={tallyTab}
          onChange={(_, v: TallyTab | null) => v && setTallyTab(v)}
        >
          <ToggleButton value="par">Par</ToggleButton>
          <ToggleButton value="fill">Fill</ToggleButton>
          <ToggleButton value="reject">Reject</ToggleButton>
          <ToggleButton value="recycle">Recycle</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flex: 1 }} />
        {saving ? (
          <Typography variant="body2" color="text.secondary">
            Saving…
          </Typography>
        ) : null}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start', mb: 2 }}>
        <TextField
          label="UPC / GTIN"
          size="small"
          value={manualUpc}
          onChange={(e) => setManualUpc(e.target.value)}
          sx={{ minWidth: 220 }}
          disabled={machineId === '' || visitStatus !== 'open'}
          slotProps={{
            htmlInput: { 'aria-label': 'UPC / GTIN' },
            input: {
              endAdornment:
                manualUpc.length > 0 ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear UPC"
                      edge="end"
                      disabled={machineId === '' || visitStatus !== 'open'}
                      onClick={() => setManualUpc('')}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
            },
          }}
        />
        <Button variant="outlined" size="medium" disabled={machineId === '' || visitStatus !== 'open'} onClick={() => void handleManualLookup()}>
          Look up
        </Button>
      </Box>

      {selectedMachine && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Editing tallies for <strong>{selectedMachine.assetId}</strong>
          {selectedMachine.title?.trim() ? ` (${selectedMachine.title.trim()})` : ''}.{' '}
          {loading ? 'Loading…' : `${lines.length} SKU(s).`}
        </Typography>
      )}

      <TableContainer>
        <Table size="small" sx={{ minWidth: 520 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48 }} />
              <TableCell>UPC</TableCell>
              <TableCell>Name</TableCell>
              <TableCell align="right">{tallyColumnHeader}</TableCell>
              {visitStatus === 'open' ? <TableCell align="center" sx={{ width: 44, p: 0 }} /> : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={visitStatus === 'open' ? 5 : 4}>
                  <Typography variant="body2" color="text.secondary">
                    No balances on this machine yet. Use Look up to add a catalog SKU.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              lines.map((line) => {
                const src = line.thumbnailUrl || line.imageUrl;
                return (
                  <TableRow key={line.rowKey}>
                    <TableCell>
                      {src ? (
                        <Avatar src={src} variant="rounded" sx={{ width: 36, height: 36 }} />
                      ) : (
                        <Avatar variant="rounded" sx={{ width: 36, height: 36 }}>
                          <Inventory2Icon fontSize="small" />
                        </Avatar>
                      )}
                    </TableCell>
                    <TableCell>{line.upc}</TableCell>
                    <TableCell>{line.name?.trim() || '—'}</TableCell>
                    <TableCell align="right">{renderQtyField(line)}</TableCell>
                    {visitStatus === 'open' ? (
                      <TableCell align="center" sx={{ width: 44, py: 0.5, px: 0 }}>
                        <IconButton
                          size="small"
                          aria-label="Remove row"
                          disabled={saving || loading || removeSubmitting}
                          onClick={() => setRemoveTargetItemId(line.inventoryItemId)}
                          sx={{ color: 'error.main' }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={removeTargetItemId != null} onClose={() => !removeSubmitting && setRemoveTargetItemId(null)}>
        <DialogTitle>Remove from visit?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove{' '}
            <strong>{linePendingRemoveDetail?.upc ?? 'this SKU'}</strong> from this machine&apos;s tally sheet?
            Pending totals saved for this line are cleared on the server; ledger balances stay the same.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={removeSubmitting} onClick={() => setRemoveTargetItemId(null)}>
            Cancel
          </Button>
          <Button color="error" variant="contained" disabled={removeSubmitting} onClick={() => void handleConfirmRemoveRow()}>
            {removeSubmitting ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
},
);

TomsVendingSiteVisitSection.displayName = 'TomsVendingSiteVisitSection';

export default TomsVendingSiteVisitSection;
